import { cn } from "@/lib/tailwind";
import { IFieldError } from "@/lib/types";
import { cva, VariantProps } from "class-variance-authority";
import React, { InputHTMLAttributes, useId } from "react";

export interface ITextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  slots?: {
    error?: IFieldError;
    label?: Partial<{
      label: React.ReactNode;
      parent: Omit<
        React.DetailedHTMLProps<React.HTMLAttributes<HTMLLabelElement>, HTMLLabelElement>,
        "children"
      >;
    }>;
    endAction?: Partial<{
      action: React.ReactNode;
      parent: Omit<
        React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
        "children"
      >;
    }>;
    startAction?: Partial<{
      action: React.ReactNode;
      parent: Omit<
        React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
        "children"
      >;
    }>;
  };
}

const inputCVA = cva(
  "h-12 w-full border-none bg-tertiary-3 p-4 text-sm text-black shadow-sm outline-none placeholder:text-[0.875rem] placeholder:font-normal placeholder:text-tertiary-4 focus:ring-2 focus:ring-black",
  {
    variants: {
      variant: { contained: undefined, outlined: "ring-2 ring-black" },
      disabled: { false: undefined, true: "disabled:cursor-not-allowed disabled:opacity-50" },
    },
    defaultVariants: {
      disabled: false,
      variant: "contained",
    },
  },
);

const Input = React.forwardRef<
  HTMLInputElement,
  Omit<ITextFieldProps, "label" | "labelProps" | "startAction" | "endAction"> &
    VariantProps<typeof inputCVA>
>(({ className, id, slots = {}, variant = "contained", ...restProps }, ref) => {
  const error = slots.error;
  const inputId = id ?? useId();

  const classes = cn(
    inputCVA({ disabled: restProps.disabled, variant }),
    error?.error && "ring-1 ring-secondary-1 focus:ring-2 focus:ring-secondary-1",
    className,
  );

  return (
    <input
      ref={ref}
      id={inputId}
      className={classes}
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${inputId}-error` : undefined}
      {...restProps}
    />
  );
});

const TextField = React.forwardRef<
  HTMLInputElement,
  ITextFieldProps & VariantProps<typeof inputCVA>
>(({ id, slots = {}, ...restProps }, ref) => {
  const { endAction = {}, error, label = {}, startAction = {} } = slots;

  if (!label?.label && !startAction?.action && !endAction?.action) {
    return <Input ref={ref} slots={slots} id={id} {...restProps} />;
  }

  const inputId = id || useId();
  const errorId = `${inputId}-error`;

  const disabled = restProps.disabled;
  const { error: errorContent, variant: errorVariant = "contained" } = error ?? {};

  return (
    <fieldset
      disabled={disabled}
      aria-describedby={errorContent ? errorId : undefined}
      className={cn(disabled && "cursor-not-allowed opacity-50")}
    >
      {label && label.label && (
        <label
          {...label.parent}
          htmlFor={inputId}
          className={cn(
            "mb-[0.125rem] block text-[0.813rem] font-normal",
            errorContent ? "text-secondary-1" : "text-black",
            label.parent?.className,
          )}
        >
          {label.label}
        </label>
      )}
      <div className={cn("relative w-full")}>
        {startAction.action && (
          <div
            {...startAction.parent}
            className={cn(
              "absolute top-1/2 -translate-y-1/2 transform ltr:left-4 rtl:right-4",
              startAction.parent?.className,
            )}
          >
            {startAction.action}
          </div>
        )}
        <Input
          ref={ref}
          id={inputId}
          slots={slots}
          {...restProps}
          className={cn(
            endAction.action && "ltr:pr-11 rtl:pl-11",
            startAction.action && "ltr:pl-11 rtl:pr-11",
            restProps.className,
          )}
        />
        {endAction.action && (
          <div
            {...endAction.parent}
            className={cn(
              "absolute top-1/2 -translate-y-1/2 transform ltr:right-1 rtl:left-1",
              endAction.parent?.className,
            )}
          >
            {endAction.action}
          </div>
        )}
      </div>
      {errorContent && (
        <p
          role="alert"
          id={errorId}
          aria-live="polite"
          {...error}
          className={cn(
            "-mx-[0.4px] text-sm text-secondary-1", // Magic number i don't know why -mx and -mt
            errorVariant === "transparent" && "mt-1",
            errorVariant === "contained" && "bg-error p-4",
            error?.className,
          )}
        >
          {errorContent}
        </p>
      )}
    </fieldset>
  );
});

export default TextField;
